/**
 * Beschriftungen, die MEHRERE Flächen der „Kundenrechnungen" brauchen
 * (`InvoiceList.jsx` → Reiter „Rechnungen", `Betrieb.jsx` → Reiter „Betrieb",
 * `InvoiceDetail.jsx` → die Seite `/invoicing/:id`).
 *
 * Eigene Datei, weil `InvoiceList.jsx` den Reiter `Betrieb.jsx` importiert:
 * holte `Betrieb.jsx` die Beschriftungen von dort, entstünde ein Importkreis.
 * Die Detailseite (S5.5-F) liest Status, Kanal, Steuerfall und Rechtsträger von
 * hier, damit Liste und Detail dasselbe Wort für denselben Wert tragen.
 *
 * Jede Tabelle deckt die GESCHLOSSENE Menge ihres Backends ganz. Gepinnt in
 * `__tests__/InvoiceList.test.jsx` (Status, BB-Zustände) und `__tests__/Betrieb.test.jsx`
 * (Dokumentart, Rechtsträger) — jeweils gegen die Quelle gelesen.
 */
import { shopifyOrderUrl } from '../../utils/shopifyAdmin'

/** `document_type` — Rechnungsdokument oder Korrektur (CHECK, `326_invoicing_core.sql`). */
export const ART_OPTIONEN = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'correction', label: 'Correction' },
]
export const ART_LABEL = Object.fromEntries(ART_OPTIONEN.map((o) => [o.value, o.label]))

/**
 * `bb_state` — ALLE acht Werte aus `bb_zustand.ZUSTAENDE`
 * (= CHECK `invoice_bb_state_check`, `backend/migrations/333_invoicing_bb_state.sql`).
 */
export const BB_LABEL = {
  manual: 'Manual',
  receipt_pending: 'Receipt pending',
  receipt_unknown: 'Receipt unclear',
  upload_abgewiesen: 'Upload rejected',
  receipt_created: 'Receipt created',
  posting_blocked: 'Posting blocked',
  posted: 'Posted',
  nicht_noetig: 'Not required',
}

/** `INVOICING_ISSUE_MODE` / `INVOICING_CORRECTION_MODE` (`issuance.py:176`): off/pilot/live. */
export const MODUS_LABEL = { off: 'Off', pilot: 'Pilot', live: 'Live' }
/** Der Modus als Text — `null` bleibt `null` (ungelesen ist nie „Aus"). */
export const modusText = (wert) => (wert == null ? null : (MODUS_LABEL[wert] || wert))

/**
 * `status` — ALLE zehn Werte des CHECK `invoice.status`
 * (`backend/migrations/326_invoicing_core.sql:58-60`).
 *
 * `cancelled` heisst „Verworfen", nicht „Storniert": der CHECK verbietet ihm
 * eine Nummer (326:94), er ist also nie ein ausgestelltes Dokument — „Storno"
 * ist im Steuerrecht aber genau das (eine Korrektur einer Rechnung).
 *
 * 🔴 **`Discarded` hier, `Dismissed` in den Warteschlangen (R16-Hinweis H1,
 * entschieden 2026-10-07).** Beide gehen auf das deutsche „Verworfen" zurück,
 * sind aber ZWEI Begriffe, nicht einer: `cancelled` ist ein **Dokumentzustand**
 * (die Rechnung wurde verworfen, bevor sie eine wurde — niemand hat gehandelt,
 * es gibt sie nicht), `dismissed` in `WatchFindings`/`RedirectReviewInbox`
 * ist eine **menschliche Triage-Handlung** (der Bediener legt
 * den Fall beiseite). „Discarded" auf einen Befund wäre falsch — der Befund
 * besteht weiter, nur die Sichtung ist erledigt; „Dismissed" auf eine Rechnung
 * wäre ebenso falsch — sie wurde nicht abgetan, sie wurde nie ausgestellt.
 * §3 „ein Wort je Begriff" ist damit erfüllt: es sind zwei Begriffe.
 */
export const STATUS_LABEL = {
  intent: 'Draft',
  issuing: 'Issuing',
  issued: 'Issued',
  mailed: 'Mailed',
  undeliverable: 'Undeliverable',
  paid: 'Paid',
  closed: 'Closed',
  cancelled: 'Discarded',
  corrected: 'Corrected',
  failed: 'Failed',
}

/**
 * Der Ton je Status (`Pill`). Die Beschriftung trägt den Zustand, nie die
 * Farbe allein (Regel 22 §2). Kontrast Text auf Füllung über dem ECHTEN Grund
 * (Paper samt `--Paper-overlay`), schwächster Ton jeweils `info`:
 *   hell   info 4,75 · ok 4,76 · warn 4,84 · err 5,91 · plain 6,36
 *   dunkel info 4,59 · ok 6,33 · warn 6,63 · err 5,92 · plain 5,81
 * Das gilt NUR auf `elevation={0}` (Tabelle und Karten der Liste, jede Fläche der
 * Detailseite): auf elevation 1 fällt `info` im Dunkelmodus auf 3,97:1 (WCAG
 * 1.4.3 AA verfehlt). Gepinnt in `__tests__/InvoiceList.test.jsx`, Test „Status-
 * und Mail-Pills erreichen 4,5:1 im Modus %s (%s), über dem echten Paper-Grund",
 * samt „KALIBRIERUNG: die Probe sieht den Dunkelmodus-Schleier …", und für die
 * Detailseite in `__tests__/InvoiceDetail.test.jsx`.
 */
export const STATUS_TON = {
  intent: 'plain',
  issuing: 'warn',
  issued: 'info',
  mailed: 'ok',
  undeliverable: 'err',
  paid: 'ok',
  closed: 'plain',
  cancelled: 'plain',
  corrected: 'plain',
  failed: 'err',
}

/** `channel` — die Herkunft der Bestellung (`channels.py`: shopify/ebay/manual). */
export const KANAL_OPTIONEN = [
  { value: 'shopify', label: 'Shopify' },
  { value: 'ebay', label: 'eBay' },
  { value: 'manual', label: 'Manual' },
]
export const KANAL_LABEL = Object.fromEntries(KANAL_OPTIONEN.map((o) => [o.value, o.label]))

/** `tax_case` (`tax_case.py`: domestic/eu_b2c_oss/eu_b2b_ic/export). */
export const STEUERFALL_OPTIONEN = [
  { value: 'domestic', label: 'Domestic', tooltip: 'Inland' },
  { value: 'eu_b2c_oss', label: 'EU B2C (OSS)', tooltip: 'OSS — One-Stop-Shop (§ 18j UStG)' },
  { value: 'eu_b2b_ic', label: 'EU B2B (intra-Community supply)', tooltip: 'ig. Lieferung' },
  { value: 'export', label: 'Export', tooltip: 'Ausfuhr' },
]
export const STEUERFALL_LABEL = Object.fromEntries(STEUERFALL_OPTIONEN.map((o) => [o.value, o.label]))
/**
 * Der deutsche Begriff JE Wert — für den Tooltip am WERT, nicht am Filter
 * (Regel-16-Hinweis H3, Welle 3 PR 3). Am Kopf trug der Rückverweis sonst nur
 * das generische „Steuerfall", während „Inland"/„Ausfuhr"/„ig. Lieferung" allein
 * im Filter-Dropdown standen.
 */
export const STEUERFALL_TOOLTIP = Object.fromEntries(STEUERFALL_OPTIONEN.map((o) => [o.value, o.tooltip]))

/**
 * Das VOLLSTAENDIGE Glossar fuer die Spaltenkopf-Zelle „Tax case" — jeder Wert
 * mit seinem deutschen Begriff, aus DENSELBEN zwei Karten wie der Wert-Tooltip
 * (eine Quelle, kein zweites Literal). Anlass: Regel-16-Warnung N1 (Welle 3
 * PR 3, Wiedervorlage 1). Die Wert-Zellen der Rechnungsliste sind seither KEIN
 * eigener Tab-Stopp mehr — eine Seite traegt bis zu 50 Zeilen, jede mit schon
 * einem Link, der Wert machte daraus zwei. Damit Tastatur und Vorlesehilfe den
 * Rueckverweis fuer JEDEN Wert trotzdem erreichen, traegt ihn der EINE
 * fokussierbare Kopf als ganzes Glossar.
 */
export const STEUERFALL_GLOSSAR = `Steuerfall — ${Object.entries(STEUERFALL_LABEL)
  .map(([wert, label]) => `${label}: ${STEUERFALL_TOOLTIP[wert]}`)
  .join(' · ')}`

/** `legal_entity` (CHECK `invoice_number_circle`, `326_invoicing_core.sql`). */
export const RECHTSTRAEGER_LABEL = { gmbh: 'GmbH', egbr: 'eGbR' }

/**
 * `gruppe_art` der gruppierten Liste (B2) — der Kopf einer Zeile je Bestellung.
 * Geschlossene Menge aus `backend/invoicing/gruppen.py::gruppenschluessel_sql`:
 * `order` (Forge-Bestellung), `name` (nur der Bestellname), `lexware` (Altbeleg
 * ohne Forge-Original), `self` (alleinstehender Beleg).
 */
export const GRUPPE_LABEL = {
  order: 'Order',
  name: 'Order name',
  lexware: 'Lexware',
  self: 'Document',
}

/** „1 correction" / „2 corrections" — die Zahl der **Korrektur**-Kinder eines Gruppenkopfs. */
export const korrekturenText = (n) => `${n} correction${n === 1 ? '' : 's'}`

/**
 * Der Hinweis am Gruppenkopf: „1 correction · Remaining 75,44" (B2).
 *
 * 🔴 **Englisch.** Der Owner sagt „Rest"; die Fläche ist englisch beschriftet, und
 * `KorrekturDialog` trägt für dieselbe Grösse schon „Amount below the remainder"
 * (Regel 22 §3: ein Wort je Begriff). Ohne Korrekturen gibt es **keinen** Hinweis
 * (kein „0 corrections"); ohne Rest (`rest == null`, kopflose Gruppe) bleibt die
 * Zahl weg — „unbekannt ist nie 0".
 *
 * 🔴 **`anzahl` ist `kopf.korrekturen`** (die Zahl der **Korrektur**-Kinder,
 * `gruppen.py:173`), NICHT `kinder.length`. Eine Bestellung mit zwei Rechnungen
 * (RF1) hat `kinder.length == 2` und `korrekturen == 1` — nur die Korrektur zählt
 * (R15-B1). Ohne jede Korrektur ist der Hinweis `null`; die Aufklappbarkeit der
 * Gruppe hängt davon **nicht** ab (sie folgt `kinder.length`).
 */
export const gruppeHinweis = (anzahl, restText) => {
  if (!anzahl) return null
  return restText == null
    ? korrekturenText(anzahl)
    : `${korrekturenText(anzahl)} · Remaining ${restText}`
}

/**
 * Die Beschriftungen der **Vorgangs-Zeitleiste** (`vorgang[].art` → englischer Text).
 *
 * 🔴 **Die EINZIGE Tabelle dieser Datei, die KEINE geschlossene Menge deckt** — und
 * das ist Absicht, kein Versehen (Regel-14-Grill W7). Der Kopf dieser Datei sagt:
 * „Jede Tabelle deckt die GESCHLOSSENE Menge ihres Backends ganz." `invoice_event.
 * event_type` ist aber freies `TEXT` (kein CHECK, `326_invoicing_core.sql:159`),
 * und das Backend reicht den **rohen** `art` durch. Unbekannte Typen fallen deshalb
 * über `vorgangLabel` auf den rohen `art` zurück: so verschwindet nichts, und ein
 * neuer Ereignistyp braucht **keine** Frontend-Änderung. Gepinnt in
 * `__tests__/InvoiceDetail.test.jsx` (unbekannter Typ zeigt den rohen `art`).
 */
export const VORGANG_LABEL = {
  issued: 'Issued',
  corrected: 'Corrected',
  sperre: 'Blocked',
  agb_nicht_erfasst: 'Terms not recorded',
  kundenbezug_geloescht: 'Customer link deleted',
  kundenbezug_unterdrueckt: 'Customer link suppressed',
  igl_handfreigabe: 'Intra-Community release',
  // BA3b: die zwei Entscheidungen ueber einen Ausfuhrnachweis
  // (`export_proof_pruefung._EREIGNIS`). Kein Eintrag in `VORGANG_TON`: eine
  // Ablehnung ist ein entschiedener Ausgang, keine gescheiterte Handlung — die
  // Beschriftung traegt ihn.
  export_proof_geprueft: 'Export proof approved',
  export_proof_abgelehnt: 'Export proof rejected',
  // BA12: die vier Ereignisse der Ausfuhr-Hinweismail (`ausfuhr_hinweis.EREIGNISSE`).
  // Reservierung (auto/hand) vor dem Senden, dann Erfolg oder Fehler; der Fehler
  // traegt in `VORGANG_TON` den Ton `error`. R16 W6 (28.10.): dasselbe Ereignis
  // (die Reservierung) mit demselben Verb, ohne Interna-Jargon („reserved"). Ein
  // Erfolg mit `an_kunde: false` beschriftet `vorgangLabel` eigens.
  ausfuhr_hinweis_auto: 'Export instructions send started (automatic)',
  ausfuhr_hinweis_hand: 'Export instructions send started (manual)',
  ausfuhr_hinweis_versandt: 'Export instructions emailed',
  ausfuhr_hinweis_fehler: 'Export instructions email failed',
  refund_decision: 'Refund decision',
  mail_vorbereitet: 'Email prepared',
  mail_versandt: 'Email sent',
  mail_abgelehnt: 'Email rejected',
  mail_vorlaeufig_gescheitert: 'Email attempt failed',
  mail_ohne_empfaenger: 'No recipient',
  mail_unterdrueckt_ebay: 'Email suppressed (eBay)',
  mail_unterdrueckt_nullbetrag: 'Email suppressed (zero amount)',
  mail_unterdrueckt_hand: 'Email suppressed (manual)',
  mail_konfigurationsfehler: 'Email configuration error',
  mail_pdf_abweichung: 'PDF mismatch',
  mail_neuversand_auto: 'Email resent (automatic)',
  mail_neuversand_hand: 'Email resent (manual)',
  mail_neuversand_fehler: 'Email resend failed',
  // 2026-10-16 („Rechnung erneut senden"): der Normalfall hat EIGENE Ereignisse
  // (Regel-14-Grill W2), damit „wie viele Resends" ueber den Ereignistyp
  // zaehlbar ist und nicht ueber das Vorhandensein eines Schlüssels.
  mail_erneut_hand: 'Invoice sent again (manual)',
  mail_erneut_fehler: 'Invoice resend failed',
  mail_admin_versandt: 'Email sent (admin)',
  bb_upload_vorbereitet: 'BB upload prepared',
  bb_beleg_angelegt: 'BB receipt created',
  bb_upload_abgewiesen: 'BB upload rejected',
  bb_beleg_uebernommen: 'BB receipt adopted',
  bb_verbucht: 'BB posted',
  bb_buchung_gesperrt: 'BB posting blocked',
  bb_buchung_nicht_noetig: 'BB posting not required',
  bb_buchung_freigegeben: 'BB posting released',
  bb_buchung_zurueckgenommen: 'BB posting withdrawn',
  bb_pilot_manuell: 'BB pilot (manual)',
  bb_upload_unbekannt: 'BB upload unknown',
  bb_laufstart_gefunden: 'BB run start found',
  bb_upload_freigegeben: 'BB upload released',
  bb_suche_ohne_treffer: 'BB search without match',
}

/**
 * R16 W6: ein Erfolg der Hinweismail an eine Adresse, die NICHT die Kundenadresse
 * ist (`an_kunde: false` — Testkopie, oder ein Versand ohne Kundenadresse), ist
 * kein schlichtes „emailed": er zaehlt nicht als „Kunde informiert" (E8).
 */
export const AUSFUHR_HINWEIS_NICHT_AN_KUNDE_LABEL = 'Export instructions emailed (not to the customer address on file)'

/**
 * Die Beschriftung eines Vorgangs-Eintrags — Rückfall ist der **rohe** `art`.
 * `nutzlast` (optional) unterscheidet nur dort, wo dasselbe Ereignis zwei
 * Bedeutungen trägt (BA12 `an_kunde`). 🔴 `Array.map` reicht den Index als
 * zweites Argument durch — eine Zahl hat kein `an_kunde`, das ist harmlos.
 */
export const vorgangLabel = (art, nutzlast) => {
  if (art === 'ausfuhr_hinweis_versandt' && nutzlast?.an_kunde === false) {
    return AUSFUHR_HINWEIS_NICHT_AN_KUNDE_LABEL
  }
  return VORGANG_LABEL[art] || art
}

// ── S4a — der Bestellname (Refund-Entscheidungen, Zahlungsstörungen) ────────
//
// 🔴 Am DATEIENDE, nicht oben: diese Datei wird mit `beschriftungen.js:NNN`
// zitiert (über zwanzig Stellen in `docs/`). Ein Block am Kopf verschiebt jede
// dieser Koordinaten; hier unten verschiebt er keine.

const OHNE_BESTELLNUMMER = '—'

/**
 * S4a — die Bestellnummer aus einer Bestellangabe (`gid://shopify/Order/<n>`).
 *
 * Der letzte Pfadteil, sonst der Rohwert; ein leerer Wert ergibt den Strich, nie
 * eine leere Zelle. Liegt hier und nicht in den Seiten, weil `bestellzeile` sie
 * braucht und beide Seiten dieselbe Regel tragen müssen.
 */
const bestellnummer = (gid) => {
  const teile = String(gid || '').split('/')
  return teile[teile.length - 1] || OHNE_BESTELLNUMMER
}

/**
 * S4a — der Bestellname mit Rückfall auf die ID.
 *
 * Der Server liefert `bestellname` additiv (`backend/invoicing/bestellname.py`);
 * fehlt er, zeigt die Fläche wie bisher den Zahlenteil der GID — nie einen leeren
 * Strich. Eine Fassung für Tabelle, Schliessdialog und Zahlungsstörungen
 * (`RefundEntscheidungen.jsx`, `Betrieb.jsx`), damit die Rückfallregel nicht in
 * zwei Kopien auseinanderläuft.
 */
export const bestellzeile = (eintrag) => eintrag?.bestellname || bestellnummer(eintrag?.bestellung)

// ── S4b — die Bestellung im Shopify-Admin (Owner-Wunsch, 03.11.2026) ────────
//
// Die Shop-Nummer ist die Kennung, mit der zugeordnet und abgeglichen wird; von
// ihr fuehrt der Verweis direkt in die Bestellung. Der Rest der Datei bleibt
// unberuehrt — sie wird mit `beschriftungen.js:NNN` zitiert.

/**
 * S4b — der Tiefenlink auf die Bestellung im Shopify-Admin, sonst `null`.
 *
 * 🔴 **Die Adresse kommt aus `utils/shopifyAdmin.js`, nicht aus einer dritten
 * Kopie.** `fall/lib/ware.js:60` und `rma/RMADetail.jsx:117` tragen dieselbe
 * Zeile; `utils/shopifyAdmin.js` ist seit dem 2026-10-04 die EINE Stelle dafuer
 * (`docs/ideas/2026-10-04-shopify-admin-konstante-sechsfach.md`) und wird hier
 * benutzt, statt eine siebte anzulegen. Die Umgebungsvariable, der Rueckfall auf
 * `kaffeemanuale` und die Schraegstrich-Kappung liegen dort.
 *
 * 🔴 **Ohne Zahl gibt es KEINEN Link.** `bestellung` ist die GID
 * (`gid://shopify/Order/<zahl>`); eine fremde Form (etwa der Name `SHOP-13820`,
 * den die Testbelege fuehren) und ein leerer Wert ergeben `null` — die Flaeche
 * zeigt dann den Text ohne Verweis, nie einen toten Link auf `…/orders/undefined`.
 * Akzeptiert wird die nackte Zahl oder ein Pfad, der auf `/Order/<zahl>` endet;
 * `gid://shopify/Refund/123` ist KEINE Bestellung und bekommt keinen Link.
 *
 * @param {{ bestellung?: string|null }} eintrag eine Zeile der Liste
 * @returns {string|null}
 */
export const bestellAdminHref = (eintrag) => {
  const roh = eintrag?.bestellung
  if (!roh) return null
  const text = String(roh)
  const zahl = /^\d+$/.test(text) ? text : (text.match(/\/Order\/(\d+)$/) || [])[1]
  return zahl ? shopifyOrderUrl(zahl) : null
}
